// companyService.js
import { db, auth } from './firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { fnUrl } from './functionsBase';

const FN_CREATE_SECONDARY_COMPANY = fnUrl('createSecondaryCompany');

// Creates a brand-new company record AND links it to the caller's own companyIds — used both by
// the Multi-Company Support onboarding flow (FeaturesPluginsSettings.js, turning the feature on
// for the first time) and by "+ Add Another Company" in Company Settings for any company after
// the second. Goes through a Cloud Function rather than a direct client write: firestore.rules'
// appUsers `update` rule requires every id in a written companyIds array to already be one of
// the caller's existing ids (so an admin can't self-assign into an existing company they don't
// belong to, e.g. the Preview Sandbox) — which also makes it impossible for any client write to
// add a genuinely brand-new id, even to the caller's own doc. The Cloud Function does the create
// + link atomically server-side instead, where that restriction doesn't apply.
export const createCompany = async ({ name, shortName, color, email, phone, address }) => {
  if (!auth.currentUser) {
    throw new Error('You must be signed in to perform this action');
  }
  const token = await auth.currentUser.getIdToken();
  const response = await fetch(FN_CREATE_SECONDARY_COMPANY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ name, shortName, color, email, phone, address })
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Request failed with status ' + response.status);
  }
  return result.companyId;
};

export const updateCompany = async (companyId, { name, shortName, color, email, phone, address }) => {
  try {
    await updateDoc(doc(db, 'companies', companyId), {
      name,
      shortName,
      color,
      email: email || '',
      phone: phone || '',
      address: address || ''
    });
  } catch (error) {
    console.error('Error updating company:', error);
    throw error;
  }
};

// Estimate/invoice document settings — kept separate from updateCompany (identity fields:
// name/shortName/color/email/phone/address) since this is a different concern with its own
// settings tab (EstimatesInvoicesSettings.js). invoiceDueDays/estimateExpirationDays are
// nullable (null = no auto-populated default, matching the app's original blank-input
// behavior before this feature existed); the four showXOnDocuments flags are nullable too so
// "never set" can be told apart from "explicitly turned off" — every reader treats anything
// other than exactly `false` as visible.
export const updateCompanySettings = async (companyId, settings) => {
  try {
    await updateDoc(doc(db, 'companies', companyId), { ...settings });
  } catch (error) {
    console.error('Error updating company settings:', error);
    throw error;
  }
};

// "Remove Company" (CompanySettingsPanel in SettingsHub.js) is a soft removal, not a hard
// delete — firestore.rules blocks deleting a companies doc outright (allow delete: if false),
// and hard-deleting one would orphan every business-data collection that carries this
// companyId (work orders, invoices, customers, etc. — there is no cascade/cleanup code
// anywhere for that). Archiving instead just hides the company from switchers/pickers
// everywhere (companyContext.js filters archived companies out of myCompanyIds) while leaving
// the company doc and every piece of historical data tied to it completely untouched and
// trivially reversible via unarchiveCompany.
export const archiveCompany = async (companyId) => {
  try {
    await updateDoc(doc(db, 'companies', companyId), { archived: true });
  } catch (error) {
    console.error('Error archiving company:', error);
    throw error;
  }
};

// Called from src/RoleLogin.js on every successful login — the only company health/activity
// signal MasterAdminConsole.js has (no last-login/last-activity field existed anywhere before
// this). Fire-and-forget by design (never awaited, never throws to the caller): a platform
// admin's "who's actually using this" view is not worth risking or delaying an actual login
// over. firestore.rules' companies match block has a dedicated onlyChangedKeys(['lastLoginAt'])
// branch so any signed-in company member can write this even though only isAdmin() can update
// the rest of the doc.
export const stampCompanyLastLogin = (companyId) => {
  if (!companyId) return;
  updateDoc(doc(db, 'companies', companyId), { lastLoginAt: Timestamp.now() }).catch((error) => {
    console.error('Error stamping company last login:', error);
  });
};

export const unarchiveCompany = async (companyId) => {
  try {
    await updateDoc(doc(db, 'companies', companyId), { archived: false });
  } catch (error) {
    console.error('Error unarchiving company:', error);
    throw error;
  }
};

// Must match functions/companyDeletion.js's own GRACE_PERIOD_DAYS exactly — this is only used
// client-side to show "scheduled for deletion on {date}", the actual enforcement (and the only
// place this number really matters) lives in the Cloud Function.
export const DELETION_GRACE_PERIOD_DAYS = 30;

// Must match functions/companyDeletion.js's own FINANCIAL_RETENTION_YEARS exactly — same
// display-only relationship as DELETION_GRACE_PERIOD_DAYS above. Invoices/estimates/payments
// outlive the rest of a deleted company's data by this much longer, for tax/accounting
// compliance (see that file's own comment for why 7 years specifically).
export const FINANCIAL_RETENTION_YEARS = 7;

// Permanently deleting a company's data (every work order, invoice, customer, photo — see
// functions/companyDeletion.js for the full list of ~40 collections + Storage folders this
// eventually cascades through) needs to be a two-step, reversible-until-it-isn't process, not
// an instant button — this just stamps the request; the actual cascade only ever runs from
// functions/companyDeletion.js's scheduled purgeExpiredCompanies, and only once
// DELETION_GRACE_PERIOD_DAYS have passed with the request never cancelled.
export const requestCompanyDeletion = async (companyId, uid) => {
  try {
    await updateDoc(doc(db, 'companies', companyId), {
      deletionRequestedAt: Timestamp.now(),
      deletionRequestedBy: uid
    });
  } catch (error) {
    console.error('Error requesting company deletion:', error);
    throw error;
  }
};

export const cancelCompanyDeletion = async (companyId) => {
  try {
    await updateDoc(doc(db, 'companies', companyId), {
      deletionRequestedAt: null,
      deletionRequestedBy: null
    });
  } catch (error) {
    console.error('Error cancelling company deletion:', error);
    throw error;
  }
};
